import { externalIntegrationsRepositorySingleton } from '@warehouse/object-browser/infra';
import { QuickbaseNavbarService } from './quickbase.nav-bar.service';
import { NavbarType } from '../quickbase-types';
import { appendEmbedParams, IframeEmbedParams } from '../quickbase-host-bridge/iframe-url-state';
import { recordPageNameFor } from '../quickbase-host-bridge/record-path-segments';

export interface QuickbaseConfig {
	type: string;
	appID: string;
	pageID: string;
	tabID: string;
	queryParams?: string;
	auth0: {
		domain: string;
		clientId: string;
	};
	serviceURL: string;
}

export const dashboardParamKeyMap: Record<NavbarType, 'utilities' | 'dashboard' | 'admin' | 'orders' | 'inventory'> = {
	[NavbarType.Utilities]: 'utilities',
	[NavbarType.Dashboard]: 'dashboard',
	[NavbarType.Admin]: 'admin',
	[NavbarType.Orders]: 'orders',
	[NavbarType.Inventory]: 'inventory',
};

export class QuickbaseServiceImpl implements QuickbaseNavbarService {
	constructor(private readonly dashboardType: NavbarType) {}

	private getConfiguration(): QuickbaseConfig {
		const integration = externalIntegrationsRepositorySingleton.get().getNavBar();
		if (!integration) {
			throw new Error('Quickbase integration not found');
		}
		const { params, serviceURL } = integration;

		const dashboardParams = params[dashboardParamKeyMap[this.dashboardType]];

		if (!dashboardParams) {
			throw new Error(`Invalid ${this.dashboardType} configuration`);
		}

		return {
			type: dashboardParams.type,
			appID: dashboardParams.appID,
			pageID: dashboardParams.pageID,
			tabID: dashboardParams.tabID,
			queryParams: dashboardParams.queryParams ?? undefined,
			auth0: {
				domain: params.auth0Domain,
				clientId: params.auth0ClientId,
			},
			serviceURL,
		};
	}

	buildEmbedUrl(embedParams?: IframeEmbedParams): string {
		const config = this.getConfiguration();

		if (embedParams?.pagename !== undefined) {
			return appendEmbedParams(
				`${config.serviceURL}db/${config.appID}?a=dbpage&pagename=${encodeURIComponent(embedParams.pagename)}`,
				{ ...embedParams, pagename: undefined },
			);
		}

		const recordPageName = recordPageNameFor(this.dashboardType);
		if (embedParams?.rid !== undefined && recordPageName !== null) {
			return appendEmbedParams(
				`${config.serviceURL}db/${config.appID}?a=dbpage&pagename=${recordPageName}`,
				embedParams,
			);
		}

		if (config.queryParams?.includes('a=dbpage')) {
			return appendEmbedParams(
				`${config.serviceURL}db/${config.appID}?${config.queryParams}&pageID=${config.pageID}`,
				embedParams ?? {},
			);
		}
		return appendEmbedParams(
			`${config.serviceURL}db/${config.appID}/${config.pageID}?a=showpage&pageIdV2=${config.tabID}&embedMode=1&ifv=1&showlinktodashboard=1&denydashboardediting=1`,
			embedParams ?? {},
		);
	}

	validateConfiguration(): boolean {
		try {
			/**
			 * If the configuration is not present getConfiguration throws
			 */
			this.getConfiguration();
			return true;
		} catch {
			return false;
		}
	}
}
